import { ButtonKeys, removeValidationFromField } from './common';

type ListElementType = { label: string; value: string };

export function setupAutocomplete(
  list: ListElementType[],
  selectedNameInput: JQuery<HTMLInputElement>,
  selectedValueInput: JQuery<HTMLInputElement>,
  button: JQuery,
  position: unknown,
  customSelectFunction?: (value: string, hasValidValue: boolean) => void,
): void {
  setupAutocompleteInternal(list, list, selectedNameInput, selectedValueInput, button, position, customSelectFunction);
}

export function setAutoCompleteSourceData(
  selectedNameInput: JQuery<HTMLInputElement>,
  selectedValueInput: JQuery<HTMLInputElement>,
  list: ListElementType[],
): void {
  selectedNameInput.autocomplete('option', 'source', list);
  selectedNameInput.autocomplete('option', 'list', list);
  clearSelectedInputWithFuncIfAppropriate(list, selectedNameInput, selectedValueInput, function () {
    selectedNameInput.val('');
    selectedValueInput.val('');
  });
}

export function setupAutocompleteInternal<T extends ListElementType>(
  source: ListElementType[] | ((request: { term: string }, response: (arg: unknown) => void) => void),
  list: ListElementType[],
  selectedNameInput: JQuery<HTMLInputElement>,
  selectedValueInput: JQuery<HTMLInputElement>,
  button: JQuery,
  position: unknown,
  customSelectFunction?: (value: string, hasValidValue: boolean) => void,
  customFilterFunction?: (list: T[]) => T[],
): void {
  selectedNameInput.autocomplete({
    source: source,
    minLength: 0,
    delay: 100,
    autoFocus: true,
    position: position,
    select: (_, ui) => {
      //disables and re-enables the clear value function as we don't want to call it after changing the value here.
      setNameAndValueInputs(
        selectedNameInput,
        selectedValueInput,
        ui.item.label,
        ui.item.value,
        customSelectFunction,
        customFilterFunction,
      );

      if (typeof customSelectFunction == 'function') {
        customSelectFunction(selectedValueInput.val() as string, true);
      }

      //we want to update it with our values.
      return false;
    },
    focus: () => {
      //doesn't want to set the value to the identifier.
      return false;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    close: (event: any) => {
      //if the event isn't of type menuselected we need to clear the input on closing.
      if (event.originalEvent == null || event.originalEvent.type != 'menuselected') {
        clearSelectedInputIfAppropriate(selectedNameInput, selectedValueInput);
      }

      //kill the original event if the user pressed escape.
      if (event.originalEvent != null && event.originalEvent.type == 'keydown' && event.originalEvent.keyCode == ButtonKeys.EscapeKey) {
        event.originalEvent.stopPropagation();
      }
    },
    search: function () {
      clearSelectedInputIfAppropriate(selectedNameInput, selectedValueInput);
    },
  });

  /*
   * Need to assign this separately rather than passing in the object above as it seems to take a copy of the array instead of passing by reference as it does here.
   * We aren't really sure why that is the case, so feel free to update this comment and/or change the code if you know the reason.
   */
  selectedNameInput.autocomplete('option', 'list', list);

  const changeValueOnNameObject = {
    nameInput: selectedNameInput,
    valueInput: selectedValueInput,
    customFunction: customSelectFunction,
    customFilterFunction: customFilterFunction,
  };
  selectedNameInput.on('keyup change', changeValueOnNameObject, changeValueOnNameChange);

  button.button({ icons: { primary: 'ui-icon-triangle-1-s' }, text: false });
  button.on('mousedown', (e) => {
    // Stop the button from triggering the autocomplete element's 'blur' event, which would
    // close it before the button's own 'click' event fires, causing it to immediately reopen
    e.preventDefault();
  });
  button.on('click', () => {
    if (selectedNameInput.autocomplete('widget').is(':visible')) {
      selectedNameInput.autocomplete('close');
    } else {
      selectedNameInput.autocomplete('search', '');
      selectedNameInput.autocomplete('widget').on('click', () => selectedNameInput.trigger('input'));
      selectedNameInput.focus();
    }
  });
}

function clearSelectedInputWithFuncIfAppropriate(
  list: ListElementType[],
  selectedNameInput: JQuery<HTMLInputElement>,
  selectedValueInput: JQuery<HTMLInputElement>,
  clearFunc: () => void,
) {
  const selectedLabel = selectedNameInput.val() as string;
  const selectedValue = selectedValueInput.val() as string;

  //if either value is empty or we can't find the combined value in the list of filers then we clear the filer value input.
  if (!doesListContainLabelValuePair(list, selectedLabel, selectedValue)) {
    clearFunc();
  }
}

function clearSelectedInputIfAppropriate(selectedNameInput: JQuery<HTMLInputElement>, selectedValueInput: JQuery<HTMLInputElement>) {
  clearSelectedInputWithFuncIfAppropriate(
    selectedNameInput.autocomplete('option', 'list'),
    selectedNameInput,
    selectedValueInput,
    function () {
      selectedValueInput.val('');
    },
  );
}

function changeValueOnNameChange(event: {
  data: {
    nameInput: JQuery<HTMLInputElement>;
    valueInput: JQuery<HTMLInputElement>;
    customFilterFunction: (list: ListElementType[]) => ListElementType[];
    customFunction: (value: string, hasValidValue: boolean) => void;
  };
}): void {
  clearSelectedInputIfAppropriate(event.data.nameInput, event.data.valueInput);

  let list = event.data.nameInput.autocomplete('option', 'list');
  if (typeof event.data.customFilterFunction === 'function') {
    list = event.data.customFilterFunction(list);
  }

  const hasValidValue = setValueForMatchingLabel(list, event.data.nameInput.val() as string, event.data.valueInput);

  if (typeof event.data.customFunction == 'function') {
    event.data.customFunction(event.data.valueInput.val() as string, hasValidValue);
  }
}

export function doesListContainLabelValuePair(
  list: ListElementType[],
  selectedLabel: string | undefined,
  selectedValue: string | undefined,
): boolean {
  if (!selectedLabel || !selectedValue) {
    return false;
  }

  return !!list.find((element) => element.label == selectedLabel && element.value == selectedValue);
}

function setValueForMatchingLabel(list: ListElementType[], selectedLabel: string, selectedValueInput: JQuery<HTMLInputElement>) {
  //if the selected value has no value then we set the value to the value for the currently given label if that exists in the list.
  if (!selectedValueInput.val()) {
    const matchingEntries = $.grep(list, function (element) {
      return element.label == selectedLabel;
    });
    //if there is only one matching entry then set the value input to the found value.
    if (matchingEntries.length == 1) {
      selectedValueInput.val(matchingEntries[0].value);

      //return true as we've updated the value.
      return true;
    }
  }

  //return false when we don't.
  return false;
}

export const listAbovePosition = {
  my: 'left bottom',
  at: 'left top',
  collision: 'fit fit',
};

export const listBelowPosition = {
  my: 'left top',
  at: 'left bottom',
  collision: 'fit fit',
};

export function setNameAndValueInputs<T extends ListElementType>(
  selectedNameInput: JQuery,
  selectedValueInput: JQuery,
  label: string,
  value: string,
  customSelectFunction?: (value: string, hasValidValue: boolean) => void,
  customFilterFunction?: (list: T[]) => T[],
): void {
  const changeValueOnNameObject = {
    nameInput: selectedNameInput,
    valueInput: selectedValueInput,
    customFunction: customSelectFunction,
    customFilterFunction: customFilterFunction,
  };

  selectedNameInput.off('change keyup', changeValueOnNameChange);
  selectedNameInput.val(label);
  selectedValueInput.val(value);
  selectedNameInput.on('change keyup', changeValueOnNameObject, changeValueOnNameChange);
  removeValidationFromField('#' + selectedNameInput.prop('id'));
}

/* Object to represent an entry in the autocomplete list */

export class AutoCompleteDataItem {
  label: string;
  value: unknown;

  constructor(label: string, data: unknown) {
    this.label = label;
    this.value = data;
  }

  getLabel(): string {
    return this.label;
  }

  getValue(): unknown {
    return this.value;
  }
}

/*---------------------------------------------------------*/
